import React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, Select, InputLabel,
    FormControl, Typography, Box, CircularProgress, Stack
} from "@mui/material";
import axios from "axios";

// Build the message using the selected employee's info
const buildTemplate = ({ leadFirstName, employeeName, employeeEmail }) => {
    const empFirst = (employeeName || "").split(" ")[0] || "Team";
    const signer = employeeName
        ? `${employeeName}${employeeEmail ? `, ${employeeEmail}` : ""}`
        : `Mars Reel Team${employeeEmail ? `, ${employeeEmail}` : ""}`;

    return `
Hi ${leadFirstName}, I hope you are well. My name is ${empFirst} from Mars Reel. I’m one of the founders. I noticed you started signing up for a free session with our academy but didn’t finish the reservation.

Just wanted to reach out and see if I could help you find the right class or answer any questions.

This is my personal cell, so feel free to text me anytime. I’d be happy to help.

You can find some upcoming sessions at https://academy.marsreel.com/map

I’m available should you have any questions or would like to chat before moving forward.

- ${signer}
`.trim();
};

export default function SmsToPhoneModal({ open, onClose, lead, onSuccess }) {
    const [employees, setEmployees] = React.useState([]);
    const [loadingEmployees, setLoadingEmployees] = React.useState(false);
    const [selectedEmpId, setSelectedEmpId] = React.useState("");
    const [smsBody, setSmsBody] = React.useState("");
    const [isSending, setIsSending] = React.useState(false);
    const [isDirty, setIsDirty] = React.useState(false); // if user hand-edits, stop auto-regenerating

    const leadFirstName = React.useMemo(() => (lead?.name || "").split(" ")[0] || "", [lead]);
    const selectedEmp = React.useMemo(
        () => employees.find(e => e._id === selectedEmpId),
        [employees, selectedEmpId]
    );

    // Load employees when opened
    React.useEffect(() => {
        if (!open) return;
        setLoadingEmployees(true);
        setIsDirty(false); // reset dirty state each time modal opens
        axios
            .get(`${process.env.REACT_APP_BACKEND_URL}/api/employees/basic`)
            .then(({ data }) => {
                const list = data || [];
                setEmployees(list);
                const first = list[0]?._id || "";
                setSelectedEmpId(first);

                // Initialize SMS body using the first employee (if present)
                const initial = buildTemplate({
                    leadFirstName,
                    employeeName: list[0]?.name || "",
                    employeeEmail: list[0]?.email || ""
                });
                setSmsBody(initial);
            })
            .catch((e) => console.error("Failed to load employees", e))
            .finally(() => setLoadingEmployees(false));
    }, [open, leadFirstName]);

    // When the selected employee changes, update the template unless user has edited
    React.useEffect(() => {
        if (!open) return;
        if (!selectedEmp || isDirty) return;
        const updated = buildTemplate({
            leadFirstName,
            employeeName: selectedEmp?.name || "",
            employeeEmail: selectedEmp?.email || ""
        });
        setSmsBody(updated);
    }, [open, selectedEmp, leadFirstName, isDirty]);

    if (!lead) return null;

    const handleChangeBody = (e) => {
        if (!isDirty) setIsDirty(true);
        setSmsBody(e.target.value);
    };

    const handleResetTemplate = () => {
        const reset = buildTemplate({
            leadFirstName,
            employeeName: selectedEmp?.name || "",
            employeeEmail: selectedEmp?.email || ""
        });
        setSmsBody(reset);
        setIsDirty(false);
    };

    const handleSend = async () => {
        if (!selectedEmpId) return alert("Select an employee to send the bundle to.");
        setIsSending(true);
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leads/${lead._id}/send-to-phone`, {
                employeeId: selectedEmpId,
                smsBody
            });
            onSuccess?.(lead._id);  // flips contacted in parent (and backend also appends note)
            onClose?.();
        } catch (e) {
            console.error("Failed to send to phone", e);
            alert("Failed to send. Check console for details.");
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Send Lead to Employee’s Phone</DialogTitle>
            <DialogContent>
                <Typography variant="body2" gutterBottom>
                    Lead: <strong>{lead.name}</strong> • {lead.phone} • {lead.email}
                </Typography>

                <Box mt={2}>
                    <FormControl fullWidth>
                        <InputLabel id="emp-label">Employee (SMS recipient)</InputLabel>
                        <Select
                            labelId="emp-label"
                            label="Employee (SMS recipient)"
                            value={selectedEmpId}
                            onChange={(e) => setSelectedEmpId(e.target.value)}
                            disabled={loadingEmployees}
                        >
                            {loadingEmployees && <MenuItem disabled>Loading…</MenuItem>}
                            {!loadingEmployees && employees.map(emp => (
                                <MenuItem key={emp._id} value={emp._id}>
                                    {emp.name} — {emp.title} ({emp.phoneE164})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {!!selectedEmp && (
                    <Box mt={1}>
                        <Typography variant="caption" color="text.secondary">
                            Using employee details in message:
                            {" "}<strong>{selectedEmp.name}</strong>
                            {selectedEmp.email ? ` • ${selectedEmp.email}` : ""}
                        </Typography>
                    </Box>
                )}

                <Box mt={2}>
                    <TextField
                        label="Preview SMS (to lead)"
                        value={smsBody}
                        onChange={handleChangeBody}
                        fullWidth
                        multiline
                        rows={8}
                    />
                    <Stack direction="row" spacing={1} mt={1}>
                        <Button size="small" onClick={handleResetTemplate}>Reset to template</Button>
                        {isDirty && (
                            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                (customized)
                            </Typography>
                        )}
                    </Stack>
                </Box>

                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    The selected employee will receive a text that includes:
                    <br />• <em>Add contact</em> link with the lead’s vCard
                    <br />• <em>Message</em> link that opens Messages to the lead with this text prefilled
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSending}>Cancel</Button>
                <Button onClick={handleSend} variant="contained" disabled={isSending || loadingEmployees}>
                    {isSending ? <CircularProgress size={20}/> : 'Send to Employee’s Phone'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
